import { useState } from 'react';
import type { HallEdits, HallLink, HallLinkKind, HallView } from '@versesverse/core';
import { useLang } from '../i18n/lang';
import { refusalWords } from '../i18n/refusal';
import { HALL_LIMITS } from '@versesverse/core';

/** The five kinds a Hall's outbound links may be, in the order a leader is likeliest to want them. */
const LINK_KINDS: HallLinkKind[] = ['youtube', 'discord', 'web', 'patreon', 'other'];

/*
 * The server's own number, not a copy of it. This said "Matches HALL_LIMITS.maxLinks on the
 * server, which refuses more" — a comment naming the thing it was a transcription of, which is the
 * clearest admission there is that nothing kept the two in step.
 */
const MAX_LINKS = HALL_LIMITS.maxLinks;

export interface HallSettingsClient {
  editHall(hallId: string, edits: HallEdits): Promise<HallView>;
}

export interface HallSettingsProps {
  client: HallSettingsClient;
  hall: HallView;
  /** Handed the saved Hall, so the page shows what the server actually stored. */
  onSaved(hall: HallView): void;
  onProblem(message: string): void;
}

/*
 * A LEADER EDITING THEIR OWN HALL — which they could not do at all.
 *
 * `PATCH /halls/:id` was answered by the server and reached by no renderer, and what hung off that
 * is larger than it sounds. A Hall's **links** are the whole of D21.2: we promise a leader that
 * their YouTube channel and their Discord are rendered plainly and go straight there, never
 * suppressed — a promise about links they had no way to add. And `memberPolicy` is what turns the
 * approval queue on, so that feature could be built, tested and shipped and never reached by
 * anybody, because the switch had no surface.
 *
 * ONE FORM, SAVED ONCE. Every field is sent together rather than each control writing on change:
 * a leader editing their community's front page is composing, not toggling, and a half-typed name
 * saved on every keystroke is a worse artefact than a save button.
 */
export function HallSettings({ client, hall, onSaved, onProblem }: HallSettingsProps): React.JSX.Element {
  const { t } = useLang();
  const [name, setName] = useState(hall.name);
  const [description, setDescription] = useState(hall.description);
  const [links, setLinks] = useState<HallLink[]>(hall.links);
  const [memberPolicy, setMemberPolicy] = useState(hall.memberPolicy);
  const [visibility, setVisibility] = useState(hall.visibility);
  const [saving, setSaving] = useState(false);

  const setLink = (index: number, patch: Partial<HallLink>): void => {
    setLinks(current => current.map((link, at) => (at === index ? { ...link, ...patch } : link)));
  };

  const save = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      /* An empty URL is a row the leader started and abandoned, not a link to nowhere. */
      const kept = links.filter(link => link.url.trim() !== '');
      onSaved(await client.editHall(hall.id, {
        name: name.trim(),
        description: description.trim(),
        links: kept,
        memberPolicy,
        visibility,
      }));
    } catch (caught) {
      onProblem(refusalWords(t, caught, 'hall.failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="hall-settings" onSubmit={event => void save(event)}>
      <label className="hall-settings-field">
        {t('hall.nameLabel')}
        <input type="text" value={name} onChange={event => setName(event.target.value)} maxLength={HALL_LIMITS.nameMaxLength} />
      </label>

      <label className="hall-settings-field">
        {t('hall.descriptionLabel')}
        <textarea value={description} onChange={event => setDescription(event.target.value)} maxLength={HALL_LIMITS.descriptionMaxLength} rows={3} />
      </label>

      {/*
        THE LINKS. D21.2's promise is that these go straight where they point, and until now it was
        a promise about something a leader could not add. The server refuses a link that is not a
        link — `javascript:` and friends — and refuses nothing about WHERE it goes, including to a
        rival.
      */}
      <fieldset className="hall-settings-links">
        <legend>{t('hall.linksLabel')}</legend>
        {links.map((link, index) => (
          <div className="hall-settings-link" key={index}>
            <select
              value={link.kind}
              onChange={event => setLink(index, { kind: event.target.value as HallLinkKind })}
              aria-label={t('hall.linkKind')}
            >
              {LINK_KINDS.map(kind => <option key={kind} value={kind}>{kind}</option>)}
            </select>
            <input
              type="url"
              value={link.url}
              onChange={event => setLink(index, { url: event.target.value })}
              placeholder="https://"
              aria-label={t('hall.linkUrl')}
            />
            <input
              type="text"
              value={link.label}
              onChange={event => setLink(index, { label: event.target.value })}
              placeholder={t('hall.linkLabel')}
              aria-label={t('hall.linkLabel')}
              maxLength={HALL_LIMITS.linkLabelMaxLength}
            />
            <button
              type="button"
              className="hall-settings-drop"
              onClick={() => setLinks(current => current.filter((_, at) => at !== index))}
            >
              {t('hall.dropLink')}
            </button>
          </div>
        ))}
        {links.length < MAX_LINKS && (
          <button
            type="button"
            className="hall-settings-add"
            onClick={() => setLinks(current => [...current, { kind: 'youtube', url: '', label: '' }])}
          >
            {t('hall.addLink')}
          </button>
        )}
      </fieldset>

      {/* The switch that makes the approval queue exist at all. */}
      <label className="hall-settings-field">
        {t('hall.doorLabel')}
        <select
          value={memberPolicy}
          onChange={event => setMemberPolicy(event.target.value === 'approval' ? 'approval' : 'open')}
        >
          <option value="open">{t('hall.doorOpen')}</option>
          <option value="approval">{t('hall.doorApproval')}</option>
        </select>
      </label>

      <label className="hall-settings-field">
        {t('hall.listingLabel')}
        <select
          value={visibility}
          onChange={event => setVisibility(event.target.value === 'unlisted' ? 'unlisted' : 'public')}
        >
          <option value="public">{t('hall.listingPublic')}</option>
          {/* Unlisted still works by its link — that is what a leader shares. It is only absent
              from the directory. */}
          <option value="unlisted">{t('hall.listingUnlisted')}</option>
        </select>
      </label>

      <button type="submit" className="hall-settings-save" disabled={saving}>
        {saving ? t('hall.saving') : t('hall.save')}
      </button>
    </form>
  );
}
